import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Search, ArrowRight, Layers, BookOpen, Hammer, Zap, Cpu, Package,
  Shield, Sun, Home, Settings, Radio, Sparkles, Scale, Columns,
  Code, Calculator, Palette, Brain
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";

interface CommandItem {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  action: () => void;
  keywords: string[];
}

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const commands: CommandItem[] = [
    { id: "settings", title: "Ajustes & Configuración del Lab", subtitle: "API Keys, Web Serial USB, LUMA, 78 Municipios PR, ChatGPT Images 2.5", icon: Settings, action: () => { setOpen(false); window.dispatchEvent(new CustomEvent("open_prometheus_settings")); }, keywords: ["settings", "ajustes", "configuracion", "api", "keys", "luma", "serial", "voz", "puerto rico", "chatgpt"] },
    { id: "home", title: t("nav.chat"), subtitle: "Chat OS · proyectos, monitor e inventario", icon: Home, action: () => navigate("/"), keywords: ["panel", "control", "dashboard", "kpi", "inicio", "chat", "os"] },
    { id: "workshop", title: t("nav.workshop"), subtitle: "Mesa de trabajo, roadmap 6 fases y prototipos", icon: Hammer, action: () => navigate("/taller"), keywords: ["taller", "workshop", "proyecto", "construir", "build", "fases", "roadmap"] },
    { id: "tools", title: t("nav.tools"), subtitle: "Suite de herramientas de ingeniería", icon: Layers, action: () => navigate("/herramientas"), keywords: ["herramientas", "tools", "fabricacion", "utilities", "suite"] },
    { id: "brain3d", title: t("nav.brain"), subtitle: "Segundo cerebro 3D · bitácora viva", icon: Brain, action: () => navigate("/brain3d"), keywords: ["brain", "cerebro", "3d", "bitacora", "wiki"] },
    { id: "cad_bridge", title: "CAD / Fotogrametría", subtitle: "Estación 4 del Taller · Fusion, FreeCAD, Blender", icon: Cpu, action: () => navigate("/taller?station=copilot"), keywords: ["cad", "cam", "fusion", "fusion 360", "blender", "freecad", "kicad", "bambu", "orcaslicer", "3d", "plano"] },
    { id: "solar", title: "Central Solar & LiFePO4 PR", subtitle: "78 Municipios, baterías 16S 48V, breakers y LUMA", icon: Sun, action: () => navigate("/herramientas/solar"), keywords: ["solar", "panel", "bateria", "baterias", "lifepo4", "luma", "energia", "renovable", "sun", "apagon"] },
    { id: "lora", title: "LoRa Mesh Station 915 MHz", subtitle: "Radioenlace táctico US915, zona de Fresnel y Meshtastic", icon: Radio, action: () => navigate("/herramientas/lora"), keywords: ["lora", "mesh", "radio", "915", "us915", "fresnel", "meshtastic", "telemetria"] },
    { id: "council", title: "El Consejo de Titanes en Vivo", subtitle: "Debate concurrente Claude, Grok, Gemini, GPT-5.8", icon: Scale, action: () => navigate("/herramientas/council"), keywords: ["consejo", "titanes", "debate", "multi-llm", "claude", "grok", "gemini", "gpt"] },
    { id: "google_pics", title: "Google Pics • Faceplates & Gabinetes", subtitle: "Serigrafía, rotulado técnico frontal, corte láser y vinilo", icon: Palette, action: () => navigate("/herramientas/google_pics"), keywords: ["faceplate", "caratula", "serigrafia", "gabinete", "rotulado", "corte laser", "google pics"] },
    { id: "arduino", title: "Suite Arduino, ESP32 & IoT", subtitle: "Simulador Wokwi, firmware C++, GPIO, UART y servos", icon: Cpu, action: () => navigate("/herramientas/arduino"), keywords: ["arduino", "esp32", "pico", "microcontrolador", "servo", "sensor", "wokwi", "gpio", "c++", "uart", "iot"] },
    { id: "mcp", title: "Hub Hardware Web Serial & MCP", subtitle: "Conexión USB directa, telemetría y sensores en tiempo real", icon: Zap, action: () => navigate("/herramientas/mcp"), keywords: ["mcp", "usb", "serial", "webserial", "hardware", "hub", "protocolo"] },
    { id: "canvas", title: "Prometheus Canvas & Artifacts 2.0", subtitle: "Editor side-by-side de C++, OpenSCAD 3D y pinouts", icon: Columns, action: () => navigate("/herramientas/canvas"), keywords: ["canvas", "artifacts", "editor", "openscad", "diff", "sandbox"] },
    { id: "gear", title: "Generador de Engranajes Involuta", subtitle: "Perfiles matemáticos de dientes, módulo y DXF/STL", icon: Settings, action: () => navigate("/herramientas/gear"), keywords: ["engranaje", "gear", "transmision", "modulo", "dientes", "mecanica"] },
    { id: "gcode", title: "Estudio CAD/CAM, Slicers & G-Code", subtitle: "Bambu/OrcaSlicer, CNC, OpenSCAD y tarifas LUMA", icon: Code, action: () => navigate("/herramientas/gcode"), keywords: ["gcode", "slicer", "bambu", "orcaslicer", "cnc", "flujo"] },
    { id: "quicktools", title: "Caja de Herramientas de Precisión", subtitle: "Ley de Ohm, divisores ADC, código colores, AWG", icon: Calculator, action: () => navigate("/herramientas/quicktools"), keywords: ["ohm", "resistencia", "colores", "awg", "calculadora", "precision", "luma"] },
    { id: "materials", title: "Laboratorio Científico & Materiales", subtitle: "PETG, Inox 316, salitre marino, hormigón ASCE 7", icon: Layers, action: () => navigate("/herramientas/materials"), keywords: ["materiales", "hormigon", "acero", "madera", "pla", "petg", "salitre", "inox 316", "ciencia"] },
    { id: "inventory", title: "Inventario & Guía de Abasto PR", subtitle: "Stock de materiales, escáner IA y ferreterías de la isla", icon: Package, action: () => navigate("/herramientas/inventory"), keywords: ["inventario", "stock", "abasto", "compras", "ferreteria", "home depot", "national lumber", "ace", "usps"] },
    { id: "journal", title: "Bitácora · El Códice Vivo", subtitle: "Registro técnico, fórmulas, planos y bitácora", icon: BookOpen, action: () => { setOpen(false); window.dispatchEvent(new CustomEvent("open_living_journal")); }, keywords: ["bitacora", "journal", "codice", "notas", "registro", "log"] },
    { id: "minds", title: t("nav.minds"), subtitle: "Tesla, Da Vinci, Curie, Arquímedes", icon: BookOpen, action: () => navigate("/mentes"), keywords: ["mentes", "inventores", "tesla", "curie", "davinci", "minds"] },
  ];

  const filtered = query.trim()
    ? commands.filter((cmd) => {
        const q = query.toLowerCase();
        return cmd.title.toLowerCase().includes(q) ||
               cmd.subtitle.toLowerCase().includes(q) ||
               cmd.keywords.some((k) => k.includes(q));
      })
    : commands;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      setOpen((prev) => !prev);
      setQuery("");
      setSelectedIdx(0);
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  const handleSelect = (cmd: CommandItem) => {
    cmd.action();
    setOpen(false);
    setQuery("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && filtered[selectedIdx]) {
      handleSelect(filtered[selectedIdx]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar herramientas, secciones, materiales..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-mono-tech text-muted-foreground border border-border rounded">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[320px] overflow-y-auto p-2 space-y-0.5">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Sin resultados para "{query}"
            </div>
          )}
          {filtered.map((cmd, idx) => (
            <button
              key={cmd.id}
              onClick={() => handleSelect(cmd)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-all ${
                idx === selectedIdx
                  ? "bg-primary/10 border border-primary/20 text-foreground"
                  : "hover:bg-card/80 border border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <cmd.icon className={`w-4 h-4 shrink-0 ${idx === selectedIdx ? "text-primary" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold block truncate">{cmd.title}</span>
                <span className="text-[9px] text-muted-foreground block truncate">{cmd.subtitle}</span>
              </div>
              <ArrowRight className={`w-3 h-3 shrink-0 ${idx === selectedIdx ? "text-primary" : "text-muted-foreground/30"}`} />
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border/30 flex items-center justify-between text-[8px] font-mono-tech text-muted-foreground uppercase tracking-wider">
          <span>↑↓ Navegar</span>
          <span>↵ Seleccionar</span>
          <span>ESC Cerrar</span>
        </div>
      </div>
    </div>
  );
};
